'use client';
import React, { useState } from 'react';
import clsx from 'clsx';
import Marquee from 'react-fast-marquee';
import {
  JenkinsShortLogo,
  AzureShortLogo,
  AlibabaShortLogo,
  AtlassianBambooShortLogo,
  AwsShortLogo,
  BazelShortLogo,
  KubernetesShortLogo,
  BuildToolOutlineShortLogo,
  CircleCiShortLogo,
  DockerShortLogo,
  GithubShortLogo,
  OnpremShortLogo,
  OpenshiftShortLogo,
  OpenstackShortLogo,
  GitlabShortLogo,
  TravisciShortLogo,
  MavenShortLogo,
  SbtShortLogo,
  GoogleCloudShortLogo,
  RancherShortLogo,
  TeamCityShortLogo,
} from '@/components/Logos';
import { Logo } from '@/components/Logos/logoTypes';
import { PillButton } from '@/components/PillButton';

interface IntegratesCardProps {
  className: string;
}

type IconKey = 'ci-systems' | 'build-tools' | 'deployment-enviroments';
type PillIcons = { [key in IconKey]: Logo[] };

const icons: PillIcons = {
  'ci-systems': [
    GithubShortLogo,
    CircleCiShortLogo,
    JenkinsShortLogo,
    TravisciShortLogo,
    TeamCityShortLogo,
    AtlassianBambooShortLogo,
    GitlabShortLogo,
  ],
  'build-tools': [MavenShortLogo, BazelShortLogo, BuildToolOutlineShortLogo, SbtShortLogo],
  'deployment-enviroments': [
    GoogleCloudShortLogo,
    KubernetesShortLogo,
    AwsShortLogo,
    DockerShortLogo,
    AlibabaShortLogo,
    AzureShortLogo,
    RancherShortLogo,
    OpenshiftShortLogo,
    OnpremShortLogo,
    OpenstackShortLogo,
  ],
};

const pillData: { label: string; key: IconKey }[] = [
  { label: 'CI Systems', key: 'ci-systems' },
  { label: 'Build Tools', key: 'build-tools' },
  { label: 'Deployment Environments', key: 'deployment-enviroments' },
];

const IntegratesCard = ({ className }: IntegratesCardProps) => {
  const [activePill, setActivePill] = useState<IconKey>('ci-systems');

  const updateActivePill = (filterName: IconKey) => {
    setActivePill(filterName);
  };

  return (
    <div
      className={clsx(
        'grid grid-cols-1 items-center gap-8 rounded-2xl bg-white pb-4 pt-9 shadow-lg md:px-10 lg:grid-cols-2 lg:py-7 xl:py-10',
        className,
      )}
    >
      <div className="flex flex-col items-center gap-6 md:gap-4 lg:items-start lg:justify-start lg:gap-7">
        <h3 className="text-h6">Develocity integrates with your</h3>
        <div className="flex flex-wrap justify-center gap-3 lg:justify-start">
          {pillData.map(pill => (
            <PillButton key={pill.key} onClick={() => updateActivePill(pill.key)} active={activePill === pill.key}>
              {pill.label}
            </PillButton>
          ))}
        </div>
      </div>
      {activePill === 'build-tools' && (
        <div className="grid grid-cols-2 place-items-center md:grid-cols-4 lg:grid-cols-2">
          {icons[activePill].map((Icon, index) => (
            <Icon key={index} className="mx-8 my-6 size-12 md:mx-10 md:my-8" />
          ))}
        </div>
      )}
      {activePill !== 'build-tools' && <IconsMarquee icons={icons} activePill={activePill} />}
    </div>
  );
};

interface IconsMarqueeProps {
  icons: PillIcons;
  activePill: IconKey;
}

function IconsMarquee({ icons, activePill }: IconsMarqueeProps) {
  const iconsArray = icons[activePill];
  const reversedIcons = iconsArray.slice().reverse();

  return (
    <div className="relative grid grid-rows-2 from-transparent to-white before:absolute before:bottom-0 before:right-0 before:top-0 before:z-10 before:w-12 before:bg-gradient-to-r before:content-[''] after:absolute after:inset-0 after:z-10 after:w-12 after:bg-gradient-to-l after:content-[''] md:grid-rows-1 lg:grid-rows-2">
      <Marquee speed={75} autoFill>
        {iconsArray.map((Icon, index) => (
          <Icon key={index} className="mx-8 my-6 size-12 md:mx-10 md:my-8" />
        ))}
      </Marquee>
      <Marquee speed={75} direction="right" className="md:!hidden lg:!flex" autoFill>
        {reversedIcons.map((Icon, index) => (
          <Icon key={index} className="mx-8 my-6 size-12 md:mx-10 md:my-8" />
        ))}
      </Marquee>
    </div>
  );
}

export default IntegratesCard;
